import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { frontDeskApi, type DocumentType } from '@/api/frontdesk';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  name: string;
  code: string;
}

export function DocumentTypesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FRONT_DESK:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FRONT_DESK:EDIT'));

  const [editing, setEditing] = useState<DocumentType | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['frontdesk', 'document-types', 'all'],
    queryFn: () => frontDeskApi.listDocumentTypes(false),
  });

  const upsert = useMutation({
    mutationFn: (values: FormShape) =>
      editing
        ? frontDeskApi.updateDocumentType(editing.id, values)
        : frontDeskApi.createDocumentType(values),
    onSuccess: () => {
      message.success(editing ? 'Document type updated' : 'Document type created');
      qc.invalidateQueries({ queryKey: ['frontdesk', 'document-types'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) =>
      frontDeskApi.toggleDocumentType(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['frontdesk', 'document-types'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
  };
  const openEdit = (row: DocumentType) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({ name: row.name, code: row.code });
  };
  const open = creating || editing != null;

  const columns: TableProps<DocumentType>['columns'] = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 120,
      render: (c: string) => <Tag color="processing" style={{ fontFamily: 'monospace' }}>{c}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (n: string) => <Typography.Text strong>{n}</Typography.Text>,
    },
    {
      title: 'Issued',
      dataIndex: 'issued',
      width: 110,
      align: 'right',
      render: (n: number) => n.toLocaleString(),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Front Desk"
        title="Document types"
        description="Catalog of certificates the school issues. Code is used in document numbering — once issued, the code is locked."
        count={{ value: data?.length ?? 0, label: 'types' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New type
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New document type'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        {open && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(v) => upsert.mutate({ ...v, code: v.code.trim().toUpperCase() })}
          >
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 100 }]}>
              <Input autoFocus placeholder="e.g. Transfer Certificate" />
            </Form.Item>
            <Form.Item
              label="Code"
              name="code"
              tooltip="Short identifier printed on issued documents (e.g. TC, CC). Cannot be changed after the first document is issued."
              rules={[{ required: true, max: 20 }]}
            >
              <Input placeholder="e.g. TC" disabled={!!editing && editing.issued > 0} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
